/**
 * PermissionUtils.js
 *
 * Utilitários para gerenciar permissões de rota baseadas no menu do usuário
 * Funciona com estrutura de menu recursiva (com submenus)
 */

/**
 * Extrai todas as URLs do menu do usuário (flat list)
 * Considera menu principal e submenus recursivamente
 *
 * @param {Object} user - Objeto user com estrutura { config: { menu: [...] } }
 * @returns {Array} Array de URLs permitidas
 *
 * @example
 * const urls = getAllowedUrls(user);
 * // → ["/manager/dashboard", "/manager/relatorios", "/manager/relatorios/pagamentos"]
 */
export const getAllowedUrls = (user) => {
  if (!user || !user.config || !user.config.menu) {
    return [];
  }

  // Fallback: Se menu vazio, garantir acesso mínimo
  if (Array.isArray(user.config.menu) && user.config.menu.length === 0) {
    console.warn('[PermissionUtils] User has empty menu, granting minimal access');
    return ['/manager/dashboard', '/manager/perfil'];
  }

  const urls = [];

  const extractUrls = (menuItems) => {
    if (!Array.isArray(menuItems)) {
      return;
    }

    menuItems.forEach(item => {
      // Filtrar URLs "#" (items pai com submenu)
      if (item.url && item.url !== '#') {
        urls.push(item.url);
      }

      // Processar submenus recursivamente
      if (item.subMenu && Array.isArray(item.subMenu)) {
        extractUrls(item.subMenu);
      }
    });
  };

  extractUrls(user.config.menu);
  return urls;
};

/**
 * Verifica se usuário tem permissão para acessar um path
 * Suporta rotas dinâmicas (:id, :uuid) com "parent path matching"
 *
 * @param {string} path - Path a verificar (ex: "/manager/dashboard")
 * @param {Array} allowedUrls - Array de URLs permitidas
 * @returns {boolean} true se tem permissão, false caso contrário
 *
 * @example
 * // Caso 1: Match exato
 * hasPermission("/manager/dashboard", ["/manager/dashboard"]) → true
 *
 * // Caso 2: Rota dinâmica
 * hasPermission("/manager/campanhas/edit/123", ["/manager/campanhas/edit"]) → true
 * // Porque "/manager/campanhas/edit" é o parent path
 *
 * // Caso 3: Sem permissão
 * hasPermission("/manager/usuarios", ["/manager/dashboard"]) → false
 */
export const hasPermission = (path, allowedUrls) => {
  // Normalizar path (remover trailing slash e espaços)
  const normalizedPath = path.replace(/\/$/, '').trim();

  // Sempre permitir página 403 para evitar loops de redirecionamento
  if (normalizedPath === '/manager/403') {
    return true;
  }

  if (!Array.isArray(allowedUrls)) {
    return false;
  }

  // Verificação 1: Match exato
  if (allowedUrls.includes(normalizedPath)) {
    return true;
  }

  // Verificação 2: Match de rotas dinâmicas
  // Exemplo: "/manager/campanhas/edit/123" → verificar "/manager/campanhas/edit"
  // Quebra o path em partes e tenta match com parent paths
  const pathParts = normalizedPath.split('/').filter(Boolean); // Remove strings vazias

  // Tentar matches de comprimentos decrescentes
  for (let i = pathParts.length; i > 0; i--) {
    const parentPath = '/' + pathParts.slice(0, i).join('/');

    if (allowedUrls.includes(parentPath)) {
      return true;
    }
  }

  return false;
};

/**
 * Verifica se uma rota é pública (não requer autenticação)
 *
 * @param {string} path - Path a verificar
 * @returns {boolean} true se é rota pública
 *
 * @example
 * isPublicRoute("/") → true
 * isPublicRoute("/login") → true
 * isPublicRoute("/manager/dashboard") → false
 */
export const isPublicRoute = (path) => {
  const publicRoutes = ['/', '/login'];
  return publicRoutes.includes(path);
};

/**
 * Obtém a URL de redirecionamento pós-login
 * Retorna a primeira URL disponível no menu do usuário
 * Se usuário tem /manager/dashboard → retorna dashboard
 * Se não tem dashboard → retorna primeira URL do menu
 * Se menu vazio → retorna /manager/perfil (fallback)
 *
 * @param {Object} user - Objeto user com estrutura { config: { menu: [...] } }
 * @returns {string} URL para redirecionamento
 *
 * @example
 * getDefaultRedirectUrl(user) → "/manager/dashboard" (se disponível)
 * getDefaultRedirectUrl(user) → "/manager/perfil" (se dashboard indisponível)
 */
export const getDefaultRedirectUrl = (user) => {
  const allowedUrls = getAllowedUrls(user);

  // Preferência 1: Dashboard (se disponível)
  if (allowedUrls.includes('/manager/dashboard')) {
    return '/manager/dashboard';
  }

  // Preferência 2: Primeira URL do menu (excluindo /manager/403)
  const firstValidUrl = allowedUrls.find(url => url !== '/manager/403');
  if (firstValidUrl) {
    return firstValidUrl;
  }

  // Fallback: Perfil (sempre permitido)
  return '/manager/perfil';
};
